/**
 * prohats-customizer.js
 * ─────────────────────────────────────────────────────────────────────────
 * PURPOSE:
 *   Web Component that encapsulates the full logic of:
 *     - components/product/product-customizer.tsx
 *     - components/product/variant-selector.tsx
 *     - components/cart/add-to-cart.tsx
 *     - components/product/wishlist-toggle.tsx
 *
 * BRIDGE PATTERN:
 *   Liquid section (server) → data-* attributes → this Custom Element (client)
 *
 * DESIGN PRINCIPLE: "Encapsulate, not rewrite"
 *   All business logic from the React components is preserved verbatim.
 *   Only the rendering layer changes: React JSX → DOM manipulation.
 *
 * DEPENDENCIES: None (zero external imports)
 *
 * CSS: prohats-tokens.css + prohats-customizer.css (loaded by theme.liquid)
 * ─────────────────────────────────────────────────────────────────────────
 */

/* ─── CONSTANTS ─────────────────────────────────────────────────────────── */

/**
 * FALLBACK_CONFIG mirrors the constant in product-customizer.tsx (lines 39-76).
 * Used when a product has no customizer.* metafields.
 */
const FALLBACK_CONFIG = {
  fixedSpecs: { Hatband: 'Black' },
  customOptions: [
    {
      name: 'Size',
      values: ['6 1/2','6 5/8','6 3/4','6 7/8','7','7 1/8','7 1/4','7 3/8','7 1/2','7 5/8','7 3/4'],
    },
    { name: 'Brim', values: ['4 1/4"'] },
    { name: 'Sweatband', values: ['Dry Tech', 'Leather'] },
  ],
  shippingMessage:
    "We don't make it until you order, all our hats are hand made, so the shipping time may vary from 14-19 business days.",
  isShopifyBacked: false,
};

/**
 * SIZE_ROWS mirrors product-customizer.tsx (lines 78-90).
 */
const SIZE_ROWS = [
  ['6 1/2', '20 1/2"', '52'],
  ['6 5/8', '20 7/8"', '53'],
  ['6 3/4', '21 1/8"', '54'],
  ['6 7/8', '21 1/2"', '55'],
  ['7',     '21 7/8"', '56'],
  ['7 1/8', '22 1/4"', '57'],
  ['7 1/4', '22 5/8"', '58'],
  ['7 3/8', '23"',     '59'],
  ['7 1/2', '23 1/2"', '60'],
  ['7 5/8', '23 7/8"', '61'],
  ['7 3/4', '24 1/4"', '62'],
];

/* ─── UTILITY FUNCTIONS ──────────────────────────────────────────────────── */

/**
 * safeJsonParse — mirrors parseJson() in product-customizer.tsx (lines 112-120)
 */
function safeJsonParse(value, fallback = null) {
  if (!value || value === 'null' || value === '') return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

/**
 * normalizeOptions — mirrors normalizeOptions() in product-customizer.tsx (lines 122-172)
 */
function normalizeOptions(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .filter(opt => opt && typeof opt === 'object')
      .map(opt => ({
        name: opt.name,
        values: Array.isArray(opt.values) ? opt.values.filter(v => typeof v === 'string') : [],
        required: typeof opt.required === 'boolean' ? opt.required : undefined,
      }))
      .filter(opt => opt.name && opt.values.length);
  }

  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([name, values]) => ({
        name,
        values: Array.isArray(values) ? values.filter(v => typeof v === 'string') : [],
      }))
      .filter(opt => opt.values.length);
  }

  return [];
}

/**
 * normalizeSpecs — mirrors normalizeSpecs() in product-customizer.tsx (lines 174-182)
 */
function normalizeSpecs(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(([, v]) => typeof v === 'string')
  );
}

/**
 * getCustomizerConfig — mirrors getCustomizerConfig() in product-customizer.tsx (lines 184-209)
 */
function getCustomizerConfig(fixedSpecsRaw, customOptionsRaw, shippingMessageRaw) {
  const fixedSpecs    = normalizeSpecs(safeJsonParse(fixedSpecsRaw, {}));
  const customOptions = normalizeOptions(safeJsonParse(customOptionsRaw, []));
  const shippingMessage = shippingMessageRaw && shippingMessageRaw !== 'null'
    ? shippingMessageRaw
    : null;

  const isShopifyBacked =
    Object.keys(fixedSpecs).length > 0 ||
    customOptions.length > 0 ||
    Boolean(shippingMessage);

  if (!isShopifyBacked) return FALLBACK_CONFIG;

  return {
    fixedSpecs: Object.keys(fixedSpecs).length ? fixedSpecs : FALLBACK_CONFIG.fixedSpecs,
    customOptions: customOptions.length ? customOptions : FALLBACK_CONFIG.customOptions,
    shippingMessage: shippingMessage || FALLBACK_CONFIG.shippingMessage,
    isShopifyBacked: true,
  };
}

/**
 * optionKey — mirrors optionKey() in product-customizer.tsx (line 211-213)
 */
function optionKey(name) {
  return name.toLowerCase().replace(/\s+/g, '-');
}

/**
 * getCategoryInfo — mirrors the useMemo in product-customizer.tsx (lines 277-304)
 */
function getCategoryInfo(tags) {
  let categoryRoute = 'felts';
  let categoryLabel = 'Felts';

  const tagsLower = tags.map(t => t.toLowerCase());

  if (tagsLower.includes('straw') || tagsLower.includes('straws')) {
    categoryRoute = 'straws'; categoryLabel = 'Straws';
  } else if (tagsLower.includes('premium') || tagsLower.includes('premium-felts') || tagsLower.includes('premium felts')) {
    categoryRoute = 'premium'; categoryLabel = 'Premium Felts';
  } else if (tagsLower.includes('dallascowboys') || tagsLower.includes('dallas cowboys')) {
    categoryRoute = 'dallascowboys/shop'; categoryLabel = 'Dallas Cowboys';
  }

  const ignoredTags = [
    'felt','felts','straw','straws','premium-felts','premium felts',
    'dallascowboys','dallas cowboys','hidden','no-customizer','customizer',
  ];
  const subCategoryTag = tags.find(
    t => !ignoredTags.includes(t.toLowerCase()) && !t.toLowerCase().startsWith('customizer:')
  );

  return { categoryRoute, categoryLabel, subCategoryTag };
}

/**
 * formatMoney — renders a localized price string.
 * Replaces the <Price> component from components/price.tsx
 */
function formatMoney(amount, currencyCode) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
    }).format(parseFloat(amount));
  } catch {
    return `${currencyCode} ${amount}`;
  }
}

/* ─── WEB COMPONENT ─────────────────────────────────────────────────────── */

class ProhatsProductCustomizer extends HTMLElement {

  /* ── Lifecycle ────────────────────────────────────────────────────────── */

  connectedCallback() {
    this._data = this._parseAttributes();

    // Initialize reactive state
    this._state = {
      variantSelections: this._getInitialVariantSelections(),
      customSelections:  this._getInitialCustomSelections(),
      quantity:          1,
      isGuideOpen:       false,
      isAddingToCart:    false,
      cartMessage:       null,
      // Wishlist: initialized from Liquid-injected server state
      isInWishlist:      this._data.isInWishlistInitial,
      isTogglingWishlist: false,
    };

    const fallback = this.querySelector('[data-customizer-fallback]');
    if (fallback) fallback.style.display = 'none';

    const loading = this.querySelector('[data-customizer-loading]');
    if (loading) loading.style.display = 'none';

    this._mount();
    this._syncVariantFromUrl();
  }

  disconnectedCallback() {
    // Clean up dialog if open
    if (this._sizeGuideDialog) {
      this._sizeGuideDialog.remove();
    }
  }

  /* ── Data Parsing ─────────────────────────────────────────────────────── */

  _parseAttributes() {
    const get = (attr) => this.dataset[attr] || '';
    const getJson = (attr, fallback) => safeJsonParse(get(attr), fallback);

    const variants = getJson('variants', []);
    const options  = getJson('options', []);
    const tags     = getJson('tags', []);

    const config = getCustomizerConfig(
      get('fixedSpecs'),
      get('customOptions'),
      safeJsonParse(get('shippingMessage'), null),
    );

    const { categoryRoute, categoryLabel, subCategoryTag } =
      getCategoryInfo(tags);

    const hasSizeOption =
      options.some(o => ['size','talla'].includes(o.name.toLowerCase())) ||
      config.customOptions.some(o => ['size','talla'].includes(o.name.toLowerCase()));

    // ── Wishlist (Customer Metafields) ─────────────────────────────────────
    // Liquid injects data-customer-wishlist as a JSON array of product handles.
    // e.g. ["el-jefe-black", "ph-11-silver"]
    const customerLoggedIn = get('customerLoggedIn') === 'true';
    const customerWishlist = getJson('customerWishlist', []);
    const productHandle    = get('productHandle');
    const wishlistToggleUrl = get('wishlistToggleUrl') || '/apps/prohats-api/wishlist/toggle';

    // Merge server state with localStorage optimistic state.
    // localStorage key: 'ph_wishlist' → { [handle]: true|false }
    let localWishlistState = {};
    try {
      localWishlistState = JSON.parse(localStorage.getItem('ph_wishlist') || '{}');
    } catch { /* ignore */ }

    // Resolution order:
    //   1. If customer is logged in → server (Liquid) is authoritative
    //   2. If not logged in → fall back to localStorage for guest preview
    let isInWishlist;
    if (customerLoggedIn) {
      isInWishlist = customerWishlist.includes(productHandle);
    } else {
      isInWishlist = Boolean(localWishlistState[productHandle]);
    }

    return {
      productId:        get('productId'),
      productTitle:     get('productTitle'),
      productHandle,
      available:        get('available') !== 'false',
      descriptionHtml:  get('descriptionHtml'),
      tags,
      priceAmount:      get('priceAmount'),
      comparePrice:     get('comparePrice'),
      priceCurrency:    get('priceCurrency'),
      firstVariantId:   get('firstVariantId'),
      firstVariantSku:  get('firstVariantSku'),
      variants,
      options,
      config,
      cartActionUrl:    get('cartActionUrl') || '/cart/add.js',
      categoryRoute,
      categoryLabel,
      subCategoryTag,
      hasSizeOption,
      wishlistEnabled:  get('wishlistEnabled') !== 'false',
      // Wishlist
      customerLoggedIn,
      customerWishlist,
      isInWishlistInitial: isInWishlist,
      wishlistToggleUrl,
      currentUrl:       get('currentUrl') || window.location.href,
    };
  }

  _getInitialVariantSelections() {
    // Mirror React's useSearchParams initial read
    const params = new URLSearchParams(window.location.search);
    const selections = {};
    const firstVariant = this._data?.variants[0];

    (this._data?.options || []).forEach(option => {
      const val = params.get(option.name.toLowerCase());
      if (val) {
        selections[option.name.toLowerCase()] = val;
      } else {
        const optVal = firstVariant?.selectedOptions.find(o => o.name.toLowerCase() === option.name.toLowerCase())?.value;
        if (optVal) {
          selections[option.name.toLowerCase()] = optVal;
        }
      }
    });
    return selections;
  }

  _getInitialCustomSelections() {
    // Mirror the useState initializer in ProductCustomizer (lines 250-257)
    const selections = {};
    (this._data?.config.customOptions || [])
      .filter(opt => opt.values.length === 1)
      .forEach(opt => { selections[opt.name] = opt.values[0]; });
    return selections;
  }

  /* ── State Management ─────────────────────────────────────────────────── */

  _setState(updates) {
    Object.assign(this._state, updates);
    this._render();
  }

  /* ── Computed Values ─────────────────────────────────────────────────────*/

  /**
   * getActiveVariant — returns the exact matching variant, or variants[0] as default
   * when no explicit selections have been made, or null when the user's selection
   * combination doesn't exist as a valid Shopify variant.
   */
  _getActiveVariant() {
    const { variants } = this._data;
    const { variantSelections } = this._state;

    // If user has made no explicit selections, return the first variant as default
    const hasAnySelection = variantSelections && Object.keys(variantSelections).length > 0;
    if (!hasAnySelection) return variants[0] || null;

    // Try to find an exact match for the current selections
    const match = variants.find(variant =>
      variant.selectedOptions.every(opt => {
        const selectedValue = variantSelections[opt.name.toLowerCase()];
        // If this option hasn't been explicitly selected, don't require a match for it
        if (!selectedValue) return true;
        return opt.value.toLowerCase() === selectedValue.toLowerCase();
      })
    );

    // Narrow further: among partial matches, prefer the one where ALL options match
    if (match) {
      const exactMatch = variants.find(variant =>
        variant.selectedOptions.every(opt => {
          const selectedValue = variantSelections[opt.name.toLowerCase()];
          if (!selectedValue) return false; // require all to match for exact
          return opt.value.toLowerCase() === selectedValue.toLowerCase();
        })
      );
      // Return exact match if found, otherwise return first partial match
      return exactMatch || match;
    }

    // No combination exists for these selections
    return null;
  }

  /**
   * getDisplayOptions — filters out options that are handled by Shopify variant selector
   * Mirrors line 306-308 of product-customizer.tsx
   */
  _getDisplayOptions() {
    const { options, config } = this._data;
    const variantOptionNames = options.map(o => o.name.toLowerCase());
    return config.customOptions.filter(
      opt => !variantOptionNames.includes(opt.name.toLowerCase())
    );
  }

  /**
   * getRealAttributes — builds the line_item.properties object
   * Mirrors lines 309-324 of product-customizer.tsx
   */
  _getRealAttributes() {
    const { config } = this._data;
    const { customSelections } = this._state;
    if (!config.isShopifyBacked) return [];

    const displayOptions = this._getDisplayOptions();

    return [
      ...Object.entries(config.fixedSpecs).map(([key, value]) => ({ key, value })),
      ...displayOptions
        .map(opt => ({ key: opt.name, value: customSelections[opt.name] }))
        .filter(attr => Boolean(attr.value)),
    ];
  }

  /* ── Wishlist (Customer Metafields) ────────────────────────────── */

  async _toggleWishlist() {
    if (this._state.isTogglingWishlist) return;

    const { productHandle, customerLoggedIn, wishlistToggleUrl } = this._data;
    const wasInWishlist = this._state.isInWishlist;

    // ── 1. Optimistic update ───────────────────────────────────────
    this._setState({ isInWishlist: !wasInWishlist, isTogglingWishlist: true });

    // ── 2. Persist to localStorage (instant, works for guests too) ───────
    try {
      const local = JSON.parse(localStorage.getItem('ph_wishlist') || '{}');
      if (!wasInWishlist) {
        local[productHandle] = true;
      } else {
        delete local[productHandle];
      }
      localStorage.setItem('ph_wishlist', JSON.stringify(local));
    } catch { /* storage not available */ }

    // ── 3. Dispatch event so other UI (wishlist page, count badge) can react ──
    this.dispatchEvent(new CustomEvent('ph:wishlist:changed', {
      bubbles: true, composed: true,
      detail: {
        handle: productHandle,
        action: wasInWishlist ? 'removed' : 'added',
        isInWishlist: !wasInWishlist,
      },
    }));

    // ── 4. If customer is logged in: persist to Customer Metafield ────────
    if (customerLoggedIn) {
      try {
        /**
         * NOTE: This POSTs to the Shopify App Proxy endpoint.
         * Phase 4 will implement the App Proxy in a minimal Node/Deno server
         * that calls the Admin API to write the customer metafield.
         *
         * The endpoint signature is:
         *   POST /apps/prohats-api/wishlist/toggle
         *   Body: { handle: string, action: 'add' | 'remove' }
         *   Returns: { success: boolean, wishlist: string[] }
         *
         * Until Phase 4, the optimistic update + localStorage ensures
         * the UI is correct; the server just can't persist across devices.
         */
        const response = await fetch(wishlistToggleUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            handle: productHandle,
            action: wasInWishlist ? 'remove' : 'add',
          }),
        });

        if (!response.ok) {
          throw new Error(`Wishlist API error: ${response.status}`);
        }

        const result = await response.json();

        // If server returns a canonical list, sync it to localStorage
        if (result.wishlist && Array.isArray(result.wishlist)) {
          const serverState = {};
          result.wishlist.forEach(h => { serverState[h] = true; });
          localStorage.setItem('ph_wishlist', JSON.stringify(serverState));

          // Also update our in-memory initial state to match server
          this._data.customerWishlist = result.wishlist;
        }

      } catch (err) {
        // ── Rollback on server error: revert optimistic update ────────────
        console.warn('[ProhatsCustomizer] Wishlist sync failed, reverting:', err);
        this._setState({ isInWishlist: wasInWishlist });

        // Also revert localStorage
        try {
          const local = JSON.parse(localStorage.getItem('ph_wishlist') || '{}');
          if (wasInWishlist) local[productHandle] = true;
          else delete local[productHandle];
          localStorage.setItem('ph_wishlist', JSON.stringify(local));
        } catch { /* ignore */ }
      }
    }

    this._setState({ isTogglingWishlist: false });
  }

  /* ── Cart Operations ─────────────────────────────────────────────────── */

  async _addToCart() {
    const { cartActionUrl } = this._data;
    const activeVariant = this._getActiveVariant();
    const attributes = this._getRealAttributes();
    const { quantity } = this._state;

    // Guard: no exact variant match means this combination doesn't exist
    if (!activeVariant) {
      this._setState({
        cartMessage: { type: 'error', text: 'This combination is not available. Please select different options.' },
      });
      return;
    }

    if (!activeVariant.availableForSale) return;

    this._setState({ isAddingToCart: true, cartMessage: null });

    const formData = {
      id: activeVariant.id,
      quantity,
      properties: Object.fromEntries(attributes.map(a => [a.key, a.value])),
    };

    try {
      const response = await fetch(cartActionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [formData] }),
      });

      if (!response.ok) {
        let errMsg = 'Error adding item. Please try again.';
        try {
          const errData = await response.json();
          if (errData && errData.description) {
            errMsg = errData.description;
          } else if (errData && errData.message) {
            errMsg = errData.message;
          }
        } catch (e) {
          // ignore
        }
        throw new Error(errMsg);
      }

      // Dispatch custom event so other components (mini-cart, header) can react
      this.dispatchEvent(new CustomEvent('ph:cart:updated', {
        bubbles: true, composed: true,
        detail: { variantId: activeVariant.id, quantity, attributes },
      }));

      this._setState({
        isAddingToCart: false,
        cartMessage: { type: 'success', text: 'Added to cart!' },
        quantity: 1,
      });

      // Auto-clear message after 3s
      setTimeout(() => this._setState({ cartMessage: null }), 3000);

    } catch (err) {
      console.error('[ProhatsCustomizer] addToCart error:', err);
      
      // Dispatch event to refresh the cart drawer in case Shopify added the partial stock
      this.dispatchEvent(new CustomEvent('ph:cart:updated', {
        bubbles: true,
        composed: true,
        detail: { variantId: activeVariant.id, quantity, attributes },
      }));

      let cleanMsg = err.message || 'Error adding item. Please try again.';
      
      // Clean and format inventory limit messages
      const patterns = [
        /only\s+(\d+)\s+left/i,
        /quedan\s+(\d+)/i,
        /only\s+(\d+)\s+items/i,
        /solo\s+(\d+)\s+artículos/i,
        /limitado\s+a\s+(\d+)/i,
        /(\d+)\s+disponibles/i,
        /(\d+)\s+available/i
      ];

      let formatted = false;
      for (const pattern of patterns) {
        const match = cleanMsg.match(pattern);
        if (match) {
          const qty = match[1];
          const isEs = document.documentElement.lang.startsWith('es') || window.location.pathname.includes('/es');
          cleanMsg = isEs ? `Solo hay ${qty} unidades disponibles de este producto.` : `Only ${qty} units available for this product.`;
          formatted = true;
          break;
        }
      }

      if (!formatted && /availability|stock|inventario|disponib/i.test(cleanMsg)) {
        const matches = cleanMsg.match(/\d+/g);
        if (matches && matches.length > 0) {
          const qty = matches[matches.length - 1];
          const isEs = document.documentElement.lang.startsWith('es') || window.location.pathname.includes('/es');
          cleanMsg = isEs ? `Solo hay ${qty} unidades disponibles de este producto.` : `Only ${qty} units available for this product.`;
        }
      }

      this._setState({
        isAddingToCart: false,
        cartMessage: { type: 'error', text: cleanMsg },
      });
    }
  }

  /* ── Variant URL Sync ───────────────────────────────────────────────────*/

  _syncVariantFromUrl() {
    // Mirror React router.replace behavior from variant-selector.tsx
    window.addEventListener('popstate', () => {
      this._setState({ variantSelections: this._getInitialVariantSelections() });
    });
  }

  _updateVariantUrl(name, value) {
    const params = new URLSearchParams(window.location.search);
    params.set(name, value);
    window.history.replaceState({}, '', `?${params.toString()}`);

    const newSelections = { ...this._state.variantSelections, [name]: value };
    this._setState({ variantSelections: newSelections });
  }

  /* ── Render ─────────────────────────────────────────────────────────────*/

  _mount() {
    // Create the root container
    this._root = document.createElement('div');
    this._root.className = 'ph-customizer-root ph-component';
    this.appendChild(this._root);
    this._render();
    this._attachEventListeners();
  }

  _render() {
    if (!this._root) return;
    this._root.innerHTML = this._buildHTML();
  }

  _buildHTML() {
    const { productTitle, firstVariantSku, hasSizeOption,
            config, categoryRoute, categoryLabel, subCategoryTag,
            priceCurrency, wishlistEnabled } = this._data;
    const { quantity, isAddingToCart, cartMessage } = this._state;
    const activeVariant  = this._getActiveVariant();
    const displayOptions = this._getDisplayOptions();

    const priceAmount = activeVariant?.price?.amount || this._data.priceAmount;
    const compareAmount = activeVariant?.compareAtPrice?.amount || (activeVariant ? null : this._data.comparePrice);

    const formattedPrice = formatMoney(priceAmount, priceCurrency);
    const hasDiscount = compareAmount && parseFloat(compareAmount) > parseFloat(priceAmount);
    const formattedComparePrice = hasDiscount ? formatMoney(compareAmount, priceCurrency) : null;

    /* ── Breadcrumb ──────────────────────────────────────────────────── */
    const breadcrumb = `
      <nav class="ph-breadcrumb" aria-label="Breadcrumb">
        <a href="/" class="ph-breadcrumb__link">Home</a>
        <span class="ph-breadcrumb__sep">/</span>
        <a href="/${categoryRoute}" class="ph-breadcrumb__link">${categoryLabel}</a>
        ${subCategoryTag ? `
          <span class="ph-breadcrumb__sep">/</span>
          <a href="/${categoryRoute}?style=${encodeURIComponent(subCategoryTag.toLowerCase())}"
             class="ph-breadcrumb__link">${subCategoryTag}</a>
        ` : ''}
        <span class="ph-breadcrumb__sep">/</span>
        <span class="ph-breadcrumb__current">${productTitle}</span>
      </nav>`;

    /* ── Header: Title, SKU, Price ───────────────────────────────────── */
    const header = `
      <div class="ph-product-header">
        ${breadcrumb}
        <h1 class="ph-product-title">${productTitle}</h1>
        ${activeVariant?.sku ? `<p class="ph-product-sku">SKU: ${activeVariant.sku}</p>` : ''}
        <div class="ph-price-row">
          <span class="ph-price-amount ${hasDiscount ? 'ph-price-amount--sale' : ''}">${formattedPrice}</span>
          ${formattedComparePrice ? `<span class="ph-price-compare">${formattedComparePrice}</span>` : ''}
          <span class="ph-price-installments">or 4 interest-free installments with Shop Pay</span>
        </div>
      </div>`;

    /* ── Size Guide Trigger ───────────────────────────────────────────── */
    const sizeGuideTrigger = hasSizeOption ? `
      <div class="ph-size-guide-trigger-row">
        <button type="button" class="ph-size-guide-trigger" data-action="open-size-guide">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" aria-hidden="true">
            <path d="M21 3H3M21 21H3M3 12h18"/>
          </svg>
          Size Guide &amp; Crown Shape
        </button>
      </div>` : '';

    /* ── Shopify Variant Selector ─────────────────────────────────────── */
    const variantSelector = this._buildVariantSelectorHTML();

    /* ── Custom Option Pickers (metafield-driven) ─────────────────────── */
    const customPickers = displayOptions
      .map(opt => this._buildOptionPickerHTML(opt))
      .join('');

    /* ── Add To Cart ──────────────────────────────────────────────────── */
    const addToCart = this._buildAddToCartHTML(activeVariant, quantity, isAddingToCart, cartMessage);

    /* ── Wishlist Toggle ─────────────────────────────────────────────── */
    const wishlist = wishlistEnabled
      ? (() => {
          const { isInWishlist, isTogglingWishlist } = this._state;
          const { customerLoggedIn } = this._data;

          // If not logged in: show button with tooltip that redirects to login
          const label = isInWishlist ? 'Saved to Wishlist' : 'Save to Wishlist';
          const heartFill = isInWishlist ? 'var(--ph-red)' : 'none';
          const heartStroke = isInWishlist ? 'var(--ph-red)' : 'currentColor';
          const cls = `ph-wishlist-toggle${isInWishlist ? ' ph-wishlist-toggle--active' : ''}${isTogglingWishlist ? ' ph-wishlist-toggle--loading' : ''}`;

          return `
            <button
              type="button"
              class="${cls}"
              data-action="toggle-wishlist"
              aria-pressed="${isInWishlist}"
              aria-label="${label}"
              ${isTogglingWishlist ? 'disabled' : ''}
              ${!customerLoggedIn ? 'data-wishlist-requires-login="true"' : ''}
              title="${!customerLoggedIn ? 'Login to save to wishlist' : label}"
            >
              <svg width="18" height="18" viewBox="0 0 24 24"
                   fill="${heartFill}" stroke="${heartStroke}"
                   stroke-width="2" aria-hidden="true">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              ${isTogglingWishlist
                ? '<span class="ph-spinner ph-spinner--sm" aria-hidden="true"></span>'
                : label
              }
            </button>`;
        })()
      : '';

    /* ── Share Buttons ───────────────────────────────────────────────── */
    const shareButtons = this._buildShareButtonsHTML();

    /* ── Accordion: Description, Specs, Shipping ─────────────────────── */
    const accordion = this._buildAccordionHTML(config);

    /* ── Assemble ─────────────────────────────────────────────────────── */
    return `
      ${header}
      ${sizeGuideTrigger}
      ${variantSelector}
      ${customPickers}
      <div class="ph-actions-group">
        ${addToCart}
        ${wishlist}
      </div>
      ${shareButtons}
      ${accordion}
    `;
  }

  /* ── Sub-renderers ───────────────────────────────────────────────────── */

  _buildVariantSelectorHTML() {
    const { options, variants } = this._data;
    const { variantSelections } = this._state;

    const hasOnlyDefault = !options.length ||
      (options.length === 1 && options[0]?.values.length === 1);
    if (hasOnlyDefault) return '';

    // Build combinations matrix
    const combinations = variants.map(v => ({
      id: v.id,
      availableForSale: v.availableForSale,
      ...Object.fromEntries(v.selectedOptions.map(o => [o.name.toLowerCase(), o.value.toLowerCase()])),
    }));

    // Track which options the user has explicitly chosen (exist in variantSelections)
    // Filtering only applies based on EXPLICITLY selected options from OTHER groups.
    const explicitSelections = variantSelections || {};

    return options.map(option => {
      const nameLower = option.name.toLowerCase();
      const isSize = nameLower === 'size' || nameLower === 'talla';
      const selectedValue = explicitSelections[nameLower] || '';
      const displayValue = selectedValue || option.values[0] || '';

      const buttons = option.values.map(value => {
        const isActive = displayValue === value;

        // Cross-filter availability:
        // A value is available if there is at least one in-stock combination
        // that matches this value AND all OTHER options that have been EXPLICITLY selected.
        const isAvailable = combinations.some(c => {
          if (!c.availableForSale) return false;
          if (c[nameLower] !== value.toLowerCase()) return false;
          // For every other option that has been explicitly selected by user, verify it matches
          return options.every(otherOpt => {
            const otherNameLower = otherOpt.name.toLowerCase();
            if (otherNameLower === nameLower) return true; // skip self
            const otherSelected = explicitSelections[otherNameLower];
            // If the user hasn't explicitly chosen this other option yet, don't filter by it
            if (!otherSelected) return true;
            return c[otherNameLower] === otherSelected.toLowerCase();
          });
        });

        let cls = 'ph-variant-btn';
        if (isSize) cls += ' ph-variant-btn--size';
        if (isActive) cls += ' ph-variant-btn--active';
        if (!isAvailable) cls += ' ph-variant-btn--unavailable';

        return `
          <button
            type="button"
            class="${cls}"
            data-action="select-variant"
            data-option-name="${nameLower}"
            data-option-value="${value}"
            ${!isAvailable ? 'aria-disabled="true"' : ''}
            title="${option.name} ${value}${!isAvailable ? ' (Out of Stock)' : ''}"
          >${value}</button>`;
      }).join('');

      return `
        <div class="ph-option-group" data-option="${nameLower}">
          <div class="ph-option-header">
            <dt class="ph-option-label">
              ${option.name}: <span class="ph-option-selected-value">${displayValue}</span>
            </dt>
          </div>
          <dd class="ph-option-values">${buttons}</dd>
        </div>`;
    }).join('');
  }

  _buildOptionPickerHTML(option) {
    const { config } = this._data;
    const { customSelections } = this._state;
    const selectedValue = customSelections[option.name] || '';
    const isPreview = !config.isShopifyBacked;
    const controlId = optionKey(option.name);

    // ≤3 values → button group; >3 values → select dropdown
    // Mirrors OptionPicker in product-customizer.tsx (lines 534-617)
    if (option.values.length <= 3) {
      const buttons = option.values.map(value => {
        const isSelected = selectedValue === value;
        let cls = 'ph-option-btn';
        if (isSelected) cls += ' ph-option-btn--active';

        return `
          <button
            type="button"
            class="${cls}"
            data-action="select-custom-option"
            data-option-name="${option.name}"
            data-option-value="${value}"
          >
            ${isSelected ? '<svg class="ph-check-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>' : ''}
            ${value}
          </button>`;
      }).join('');

      return `
        <section class="ph-custom-option ph-custom-option--buttons" data-option-section="${controlId}">
          <div class="ph-custom-option__header">
            <h3 class="ph-option-label">${option.name}${option.required ? ' *' : ''}</h3>
            ${isPreview ? '<span class="ph-badge-preview">Preview</span>' : ''}
          </div>
          <div class="ph-option-btn-group">${buttons}</div>
        </section>`;
    }

    // Select dropdown
    const selectOptions = option.values.map(value =>
      `<option value="${value}" ${selectedValue === value ? 'selected' : ''}>
        ${value}
      </option>`
    ).join('');

    return `
      <section class="ph-custom-option ph-custom-option--select" data-option-section="${controlId}">
        <label for="${controlId}" class="ph-option-label">
          ${option.name}${option.required ? ' *' : ''}
        </label>
        <div class="ph-select-wrapper">
          <select
            id="${controlId}"
            class="ph-select"
            data-action="select-custom-option-dropdown"
            data-option-name="${option.name}"
          >
            <option value="">Select ${option.name.toLowerCase()}</option>
            ${selectOptions}
          </select>
        </div>
        ${isPreview ? '<p class="ph-preview-note">Preview only — configure metafields in Shopify Admin.</p>' : ''}
      </section>`;
  }

  _buildAddToCartHTML(activeVariant, quantity, isAdding, cartMessage) {
    const hasExactVariant = Boolean(activeVariant?.id);
    const isAvailable = hasExactVariant && activeVariant.availableForSale && this._data.available;

    let buttonText = 'Add To Cart';
    let buttonDisabled = false;
    let buttonClass = 'ph-btn-primary ph-btn-add-to-cart';

    if (!hasExactVariant) {
      buttonText = 'Combination Unavailable';
      buttonDisabled = true;
    } else if (!isAvailable) {
      buttonText = 'Out of Stock';
      buttonDisabled = true;
    } else if (isAdding) {
      buttonText = 'Adding...';
      buttonDisabled = true;
    }

    const quantityStepper = isAvailable && hasExactVariant ? `
      <div class="ph-quantity-stepper">
        <button type="button" class="ph-qty-btn" data-action="decrement-qty"
          ${quantity <= 1 ? 'disabled' : ''} aria-label="Decrease quantity">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" aria-hidden="true">
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </button>
        <span class="ph-qty-value" aria-live="polite">${quantity}</span>
        <button type="button" class="ph-qty-btn" data-action="increment-qty"
          aria-label="Increase quantity">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </button>
      </div>` : '';

    const messageHtml = cartMessage ? `
      <p class="ph-cart-message ph-cart-message--${cartMessage.type}" aria-live="polite">
        ${cartMessage.text}
      </p>` : '';

    return `
      <div class="ph-add-to-cart">
        <div class="ph-add-to-cart__row">
          ${quantityStepper}
          <div class="ph-add-to-cart__btn-wrapper">
            <button
              type="button"
              class="${buttonClass}"
              data-action="add-to-cart"
              ${buttonDisabled ? 'disabled' : ''}
              aria-label="Add to cart"
            >
              ${isAdding
                ? '<span class="ph-spinner" aria-hidden="true"></span>'
                : '<svg class="ph-plus-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>'
              }
              ${buttonText}
            </button>
          </div>
        </div>
        ${messageHtml}
      </div>`;
  }

  _buildShareButtonsHTML() {
    const { productTitle, currentUrl } = this._data;
    const encoded = encodeURIComponent(currentUrl);
    const encodedTitle = encodeURIComponent(productTitle);

    return `
      <div class="ph-share-buttons">
        <span class="ph-share-label">Share:</span>
        <div class="ph-share-icons">
          <button type="button" class="ph-share-btn" data-action="native-share"
            title="Share product">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" aria-hidden="true">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          </button>
          <a href="https://www.facebook.com/sharer/sharer.php?u=${encoded}"
             target="_blank" rel="noopener noreferrer"
             class="ph-share-btn" title="Share on Facebook" aria-label="Share on Facebook">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
            </svg>
          </a>
          <a href="https://twitter.com/intent/tweet?url=${encoded}&text=${encodedTitle}"
             target="_blank" rel="noopener noreferrer"
             class="ph-share-btn" title="Share on X" aria-label="Share on X">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </a>
          <a href="https://pinterest.com/pin/create/button/?url=${encoded}&description=${encodedTitle}"
             target="_blank" rel="noopener noreferrer"
             class="ph-share-btn" title="Pin on Pinterest" aria-label="Pin on Pinterest">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
            </svg>
          </a>
        </div>
      </div>`;
  }

  _buildAccordionHTML(config) {
    const { descriptionHtml } = this._data;

    const specsHtml = Object.keys(config.fixedSpecs).length > 0
      ? Object.entries(config.fixedSpecs).map(([label, value]) => `
          <div class="ph-spec-item">
            <dt class="ph-spec-label">${label}</dt>
            <dd class="ph-spec-value">${value}</dd>
          </div>`).join('')
      : '';

    return `
      <div class="ph-accordion">
        ${descriptionHtml ? `
          <div class="ph-accordion-item" data-accordion>
            <button class="ph-accordion-trigger" data-action="toggle-accordion" type="button">
              Description
              <span class="ph-accordion-icon" aria-hidden="true"></span>
            </button>
            <div class="ph-accordion-panel" hidden>
              <div class="ph-prose">${descriptionHtml}</div>
            </div>
          </div>` : ''}

        ${specsHtml ? `
          <div class="ph-accordion-item" data-accordion>
            <button class="ph-accordion-trigger" data-action="toggle-accordion" type="button">
              Specifications
              <span class="ph-accordion-icon" aria-hidden="true"></span>
            </button>
            <div class="ph-accordion-panel" hidden>
              <dl class="ph-specs-grid">${specsHtml}</dl>
            </div>
          </div>` : ''}

        <div class="ph-accordion-item" data-accordion>
          <button class="ph-accordion-trigger" data-action="toggle-accordion" type="button">
            Free Shipping &amp; Returns
            <span class="ph-accordion-icon" aria-hidden="true"></span>
          </button>
          <div class="ph-accordion-panel" hidden>
            <p class="ph-shipping-text">${config.shippingMessage}</p>
          </div>
        </div>
      </div>`;
  }

  /* ── Size Guide Dialog ────────────────────────────────────────────────── */

  _openSizeGuide() {
    if (this._sizeGuideDialog) {
      this._sizeGuideDialog.showModal();
      return;
    }

    // Build the dialog (mirrors SizeGuideDialog in product-customizer.tsx lines 619-743)
    const dialog = document.createElement('dialog');
    dialog.className = 'ph-size-guide-dialog';
    dialog.setAttribute('aria-label', 'Size Guide & Crown Shape');

    const sizeRows = SIZE_ROWS.map(([size, inches, cm]) => `
      <tr class="ph-size-row">
        <td class="ph-size-cell ph-size-cell--bold">${size}</td>
        <td class="ph-size-cell">${inches}</td>
        <td class="ph-size-cell">${cm}</td>
      </tr>`).join('');

    dialog.innerHTML = `
      <div class="ph-dialog-inner">
        <div class="ph-dialog-header">
          <h2 class="ph-dialog-title">Size Guide &amp; Crown Shape</h2>
          <button type="button" class="ph-dialog-close" data-action="close-size-guide"
            aria-label="Close size guide">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <table class="ph-size-table">
          <thead>
            <tr>
              <th class="ph-size-th">Hat Size</th>
              <th class="ph-size-th">Head (Inches)</th>
              <th class="ph-size-th">Head (cm)</th>
            </tr>
          </thead>
          <tbody>${sizeRows}</tbody>
        </table>

        <p class="ph-size-note">
          The table above provides a conversion of our hat sizes in inches and centimeters.
        </p>

        <section class="ph-crown-images">
          <h3 class="ph-crown-title">No measuring tape?</h3>
          <p class="ph-crown-desc">
            Use a snapback hat and match your preferred fit to the provided chart.
            If your measurement falls between sizes, opt for the larger size.
          </p>
        </section>
      </div>`;

    document.body.appendChild(dialog);
    this._sizeGuideDialog = dialog;
    dialog.showModal();

    // Close on backdrop click
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });

    // Close on close button click
    dialog.querySelector('[data-action="close-size-guide"]')
      ?.addEventListener('click', () => dialog.close());
  }

  /* ── Event Delegation ───────────────────────────────────────────────── */

  _attachEventListeners() {
    // Single delegated listener on the root container
    this._root.addEventListener('click', (e) => this._handleClick(e));
    this._root.addEventListener('change', (e) => this._handleChange(e));
  }

  _handleClick(e) {
    const target = e.target.closest('[data-action]');
    if (!target) return;

    const action = target.dataset.action;

    switch (action) {
      case 'select-variant':
        this._updateVariantUrl(
          target.dataset.optionName,
          target.dataset.optionValue
        );
        break;

      case 'select-custom-option':
        this._setState({
          customSelections: {
            ...this._state.customSelections,
            [target.dataset.optionName]: target.dataset.optionValue,
          },
        });
        break;

      case 'add-to-cart':
        this._addToCart();
        break;

      case 'increment-qty':
        this._setState({ quantity: this._state.quantity + 1 });
        break;

      case 'decrement-qty':
        this._setState({ quantity: Math.max(1, this._state.quantity - 1) });
        break;

      case 'toggle-accordion':
        const item = target.closest('[data-accordion]');
        const panel = item?.querySelector('.ph-accordion-panel');
        if (panel) {
          const isOpen = !panel.hidden;
          panel.hidden = isOpen;
          target.setAttribute('aria-expanded', !isOpen);
          item.classList.toggle('ph-accordion-item--open', !isOpen);
        }
        break;

      case 'open-size-guide':
        this._openSizeGuide();
        break;

      case 'native-share':
        if (navigator.share) {
          navigator.share({ title: this._data.productTitle, url: this._data.currentUrl })
            .catch(() => {});
        } else {
          navigator.clipboard.writeText(this._data.currentUrl)
            .then(() => alert('Link copied to clipboard!'))
            .catch(() => {});
        }
        break;

      case 'toggle-wishlist':
        if (target.dataset.wishlistRequiresLogin === 'true') {
          // Guest: redirect to login with return URL
          window.location.href = `/account/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        } else {
          this._toggleWishlist();
        }
        break;
    }
  }

  _handleChange(e) {
    const target = e.target.closest('[data-action]');
    if (!target) return;

    if (target.dataset.action === 'select-custom-option-dropdown') {
      this._setState({
        customSelections: {
          ...this._state.customSelections,
          [target.dataset.optionName]: target.value,
        },
      });
    }
  }
}

/* ─── REGISTER ───────────────────────────────────────────────────────────── */
if (!customElements.get('prohats-product-customizer')) {
  customElements.define('prohats-product-customizer', ProhatsProductCustomizer);
}
